import React, { KeyboardEvent as ReactKeyboardEvent, useCallback, useEffect, useRef, useState } from 'react'

import { css, Global, keyframes } from '@emotion/react'
import styled from '@emotion/styled'
import FocusLock from 'react-focus-lock'

import { useMediaQuery } from '../../hooks/useMediaQuery'
import { usePreventScroll } from '../../hooks/usePreventScroll'
import { breakpoints, spacing } from '../../theme'
import Dimmer from '../Arcade/Dimmer/Dimmer'
import { Flex } from '../layout/Flex'
import { ArcadeModalActionProvider } from './ArcadeModalActionProvider'
import { ArcadeModalContextProvider } from './ArcadeModalContext'
import ArcadeModalHeader from './ArcadeModalHeader'
import { ArcadeModalPane } from './ArcadeModalPane'

export type ArcadeModalVariant =
  | 'default'
  | 'receipt'
  | 'fullscreen'
  | 'taxes-applet'
  | 'support-search'
  | 'legal-disclosure'

/**
 * Responsive modal variants adapt their presentation:
 * - Desktop/tablet: Centered dialog (max-width, rounded corners, margins)
 * - Mobile: Viewport-filling by default (can be overridden with alwaysDialog prop)
 *
 * This is distinct from the 'fullscreen' variant which is a fixed-header pane on all viewports.
 */
type ResponsiveModalVariant = Exclude<ArcadeModalVariant, 'fullscreen'>

const DEFAULT_PADDING = {
  paddingWithoutHeader: { mobile: spacing.defaultContent, tablet: '4.5rem' },
  paddingWithHeader: {
    mobile: `${spacing.m} ${spacing.l} ${spacing.defaultContent}`,
    tablet: `${spacing.empty} ${spacing.xxl} ${spacing.defaultContent}`,
  },
}

const RESPONSIVE_VARIANT_DEFAULTS: Record<
  ResponsiveModalVariant,
  {
    allowOverflow: boolean
    allowContentScroll: boolean
    paddingWithoutHeader: { mobile: string; tablet: string }
    paddingWithHeader: { mobile: string; tablet: string }
    width: string
  }
> = {
  default: {
    width: '31rem',
    paddingWithoutHeader: DEFAULT_PADDING.paddingWithoutHeader,
    paddingWithHeader: DEFAULT_PADDING.paddingWithHeader,
    allowOverflow: true,
    allowContentScroll: true,
  },
  receipt: {
    width: '25rem',
    allowOverflow: false,
    paddingWithoutHeader: DEFAULT_PADDING.paddingWithoutHeader,
    paddingWithHeader: DEFAULT_PADDING.paddingWithHeader,
    allowContentScroll: true,
  },
  'taxes-applet': {
    width: '55rem',
    allowOverflow: true,
    paddingWithoutHeader: DEFAULT_PADDING.paddingWithoutHeader,
    paddingWithHeader: DEFAULT_PADDING.paddingWithHeader,
    allowContentScroll: true,
  },
  'support-search': {
    width: '45rem',
    allowOverflow: true,
    paddingWithoutHeader: DEFAULT_PADDING.paddingWithoutHeader,
    paddingWithHeader: DEFAULT_PADDING.paddingWithHeader,
    allowContentScroll: true,
  },
  'legal-disclosure': {
    width: '39rem',
    allowOverflow: false,
    paddingWithoutHeader: {
      mobile: `0 ${spacing.defaultContent} 3.5rem 2.5rem`,
      tablet: `0 ${spacing.defaultContent} 3.5rem ${spacing.defaultContent}`,
    },
    paddingWithHeader: DEFAULT_PADDING.paddingWithHeader,
    allowContentScroll: true,
  },
}

const BodyOverFlowHidden = css`
  body {
    overflow: hidden;
  }
`

const popUp = keyframes`
    0% {
        transform: translateY(1rem);
    }
    100% {
        transform: translateY(0);
    }
`

const ResponsiveContainer = styled.div<{
  allowOverflow?: boolean
  contentPadding?: string
  enableAnimation?: boolean
  enforceResponsiveMobileDialog?: boolean
  hasHeader?: boolean
  isDesktop?: boolean
  maxWidth?: string
  responsiveVariant?: ResponsiveModalVariant
}>`
  /* Base styles - apply to all viewports */
  overflow: ${({ allowOverflow }) => (allowOverflow ? 'visible' : 'hidden')};
  width: 100%;
  margin: 0 auto;
  ${({ enableAnimation }) =>
    enableAnimation &&
    css`
      animation: ${popUp} 200ms forwards;
    `};
  background: ${props => props.theme.arcadeColors.component.modalBackground};
  position: relative;
  display: flex;
  flex-direction: column;
  height: 100%;
  outline: 1rem solid ${props => props.theme.arcadeColors.component.modalBackground};

  /* Mobile viewport-filling styles (default on mobile) */

  /* Padding only applied when modal has no header AND contentPadding is not explicitly set */
  ${({ hasHeader, contentPadding, responsiveVariant = 'default' }) =>
    !hasHeader &&
    contentPadding === undefined &&
    css`
      padding: ${RESPONSIVE_VARIANT_DEFAULTS[responsiveVariant].paddingWithoutHeader.mobile};
    `}

  /* Desktop/tablet OR mobile with enforceResponsiveMobileDialog (dialog mode) */
  ${props => {
    const shouldApplyDialogStyles = props.isDesktop || (!props.isDesktop && props.enforceResponsiveMobileDialog)
    if (!shouldApplyDialogStyles) {
      return null
    }

    const responsiveVariantDefaults = RESPONSIVE_VARIANT_DEFAULTS[props.responsiveVariant ?? 'default']

    return css`
      max-width: ${props.maxWidth ?? responsiveVariantDefaults.width};
      height: auto;
      max-height: 80vh;
      margin-top: 10vh;
      margin-bottom: 10vh;
      border-radius: 1.5rem;
      overflow: ${responsiveVariantDefaults.allowOverflow ? 'visible' : 'hidden'};
      box-shadow: ${props.theme.dropShadows.dialog};
      outline: 0;

      /* Padding only applied when modal has no header AND contentPadding is not explicitly set */
      ${!props.hasHeader &&
      props.contentPadding === undefined &&
      css`
        padding: ${responsiveVariantDefaults.paddingWithoutHeader.tablet};
      `}

      /* Adjust max-width on mobile to create spacing between Container and viewport edge */
      ${!props.isDesktop &&
      css`
        max-width: calc(${props.maxWidth ?? '100%'} - ${props.theme.spacing.m} * 2);
      `}
    `
  }}
`

const Content = styled.div<{
  contentPadding?: string
  allowContentScroll?: boolean
  enforceResponsiveMobileDialog?: boolean
  hasHeader?: boolean
  isDesktop?: boolean
  responsiveVariant?: ResponsiveModalVariant
}>`
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
  overflow-y: ${({ allowContentScroll }) => (allowContentScroll ? 'auto' : 'visible')};
  -webkit-overflow-scrolling: touch;
  padding: ${({ contentPadding, theme: { spacing }, hasHeader, responsiveVariant = 'default' }) =>
    contentPadding ??
    `${hasHeader ? RESPONSIVE_VARIANT_DEFAULTS[responsiveVariant].paddingWithHeader.mobile : spacing.empty}`};
  position: relative;
  flex-grow: 1;

  ${props => props.theme.typography.body}

  ${props => {
    if (props.enforceResponsiveMobileDialog || props.isDesktop) {
      const styles = []

      if (props.contentPadding) {
        styles.push(`padding: ${props.contentPadding};`)

        // Apply border-radius when content extends to modal edges (contentPadding === '0')
        if (props.contentPadding === '0') {
          if (props.hasHeader) {
            // Header has top corners, Content needs bottom corners
            styles.push('border-radius: 0 0 1.5rem 1.5rem;')
          } else {
            // No header, Content needs all corners
            styles.push('border-radius: 1.5rem;')
          }
        }
      } else if (props.hasHeader) {
        styles.push(
          `padding: ${RESPONSIVE_VARIANT_DEFAULTS[props.responsiveVariant ?? 'default'].paddingWithHeader.tablet}`
        )
      }

      return styles.join('\n')
    }
    return ''
  }}
`

const ShadowPosition = styled.div`
  position: relative;
  height: 0;
  pointer-events: none;
`

const ShadowClip = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 1rem;
  overflow: hidden;
`

const Shadow = styled.div<{ active: boolean }>`
  width: 100%;
  left: 0;
  right: 0;
  top: -5rem;
  height: 5rem;
  position: absolute;
  transition: opacity 100ms;
  box-shadow: ${props => props.theme.dropShadows.topBar};
  opacity: ${props => (props.active ? 1 : 0)};
`

const ScrollDetector = styled.div`
  width: 1px;
  height: 1px;
  position: absolute;
  top: 0;
  left: 0;
`

export interface ArcadeModalProps {
  /**
   * When true, allows content to break out of modal boundaries (tooltips, popovers).
   * When false, clips all content at container edges.
   * If not specified, uses the variant's default overflow behavior from RESPONSIVE_VARIANT_DEFAULTS.
   * Explicitly passing this prop will override the variant's default.
   */
  allowOverflow?: boolean
  /**
   * When true, enables vertical scrolling of modal content.
   * When false, content will expand to its full height.
   * If not specified, uses the variant's default scroll behavior from RESPONSIVE_VARIANT_DEFAULTS.
   * Explicitly passing this prop will override the variant's default.
   */
  allowContentScroll?: boolean
  /**
   * Accessibility label for the modal dialog.
   * Used as the aria-label attribute on the modal container.
   */
  ariaLabel: string
  /**
   * Back button configuration.
   * When provided, a back button will be displayed in the modal header.
   */
  backButtonProps?: {
    /**
     * Callback for back navigation.
     */
    onBack: () => void
    /**
     * Accessibility label for the back button.
     */
    ariaLabel: string
  }
  /**
   * Content to render inside the modal.
   */
  children?: React.ReactNode
  /**
   * Close button configuration.
   * When provided, enables all close behaviors: displays close button, allows ESC key, and allows dimmer click to close.
   * When not provided, the modal cannot be closed by the user (useful for required actions/flows).
   */
  closeButtonProps?: {
    /**
     * Callback when the modal is closed (via close button, ESC key, or dimmer click).
     */
    onClose: () => void
    /**
     * Accessibility label for the close button.
     */
    ariaLabel: string
  }
  /**
   * Custom padding for modal content.
   * Overrides the default padding from the variant.
   */
  contentPadding?: string
  /**
   * When true, enables both the background fade-in and modal pop-up animations.
   * When false, animations are disabled (useful for testing, accessibility, or performance reasons).
   * @default true
   */
  'data-testid'?: string
  enableAnimation?: boolean
  /**
   * When true, enforces dialog mode on mobile devices instead of the default viewport-filling behavior.
   * Desktop always uses dialog mode regardless of this prop.
   * @default false
   */
  enforceResponsiveMobileDialog?: boolean
  /**
   * Maximum width for the modal on desktop/tablet viewports.
   * Overrides the variant's default width.
   */
  maxWidth?: string
  /**
   * Title in modal action header.
   */
  title?: string
  /**
   * The variant of the modal which determines its default styling and behavior.
   */
  variant?: ArcadeModalVariant
}

function useScrolled() {
  const target = useRef<HTMLElement | null>()
  const root = useRef<HTMLElement | null>()
  const [hasScrolled, setScrolled] = useState(false)

  useEffect(() => {
    // Only observe if target element exists (won't exist for fullscreen variants)
    if (!target.current) {
      return
    }

    const observer = new IntersectionObserver(entries => setScrolled(!entries[0].isIntersecting), {
      root: root.current,
    })
    observer.observe(target.current)
    return () => observer.disconnect()
  }, [])

  return { hasScrolled, root, target }
}

const ArcadeModal = React.forwardRef<HTMLDivElement, ArcadeModalProps>(
  (
    {
      allowContentScroll,
      allowOverflow,
      ariaLabel,
      backButtonProps,
      children,
      closeButtonProps,
      contentPadding,
      'data-testid': dataTestId,
      enableAnimation = true,
      enforceResponsiveMobileDialog,
      maxWidth,
      title,
      variant = 'default',
    },
    ref
  ) => {
    const { hasScrolled, target, root } = useScrolled()
    const isDesktop = useMediaQuery(breakpoints.tablet)

    // On mobile, prevent body scroll and maintain scroll position while modal is open.
    // Fixes iOS 26 bug where body remains scrollable under the modal.
    // Can be removed if the bug is fixed in a future iOS version.
    usePreventScroll({ prevent: !isDesktop })

    const handleKeyDown = useCallback(
      (event: ReactKeyboardEvent<HTMLDivElement>) => {
        if (event.key === 'Escape' && closeButtonProps) {
          closeButtonProps.onClose()
        }
      },
      [closeButtonProps]
    )

    if (variant === 'fullscreen') {
      return (
        <ArcadeModalPane
          ariaLabel={ariaLabel}
          backButtonProps={backButtonProps}
          closeButtonProps={closeButtonProps}
          data-testid={dataTestId ?? 'arcade-modal-fullscreen'}
          ref={ref}
        >
          {children}
        </ArcadeModalPane>
      )
    }

    // Fullscreen variant returns above, so we know we are left with the responsive variants
    const responsiveVariant = variant as ResponsiveModalVariant
    const hasHeader = !!closeButtonProps || !!backButtonProps

    return (
      <>
        <Global styles={BodyOverFlowHidden} />
        <FocusLock autoFocus={false}>
          <Dimmer
            onClick={() => {
              if (closeButtonProps) {
                closeButtonProps.onClose()
              }
            }}
            alignItems={responsiveVariant === 'support-search' ? 'flex-start' : 'center'}
            enableAnimation={enableAnimation}
            justifyContent="center"
            data-testid="modal-dimmer"
          >
            <ResponsiveContainer
              allowOverflow={allowOverflow ?? RESPONSIVE_VARIANT_DEFAULTS[responsiveVariant].allowOverflow}
              aria-label={ariaLabel}
              aria-modal="true"
              contentPadding={contentPadding}
              enableAnimation={enableAnimation}
              enforceResponsiveMobileDialog={enforceResponsiveMobileDialog}
              hasHeader={hasHeader}
              isDesktop={isDesktop}
              maxWidth={maxWidth}
              onClick={event => event.stopPropagation()}
              onKeyDown={handleKeyDown}
              ref={element => {
                root.current = element
                if (typeof ref === 'function') {
                  ref(element)
                } else if (ref) {
                  ref.current = element
                }
              }}
              responsiveVariant={responsiveVariant}
              role="dialog"
              data-testid={dataTestId ?? 'modal-background'}
            >
              <ArcadeModalContextProvider>
                <ArcadeModalActionProvider>
                  <ArcadeModalHeader
                    backButtonProps={backButtonProps}
                    closeButtonProps={closeButtonProps}
                    enforceResponsiveMobileDialog={enforceResponsiveMobileDialog}
                    isDesktop={isDesktop}
                    title={title}
                  />

                  <ShadowPosition>
                    <ShadowClip>
                      <Shadow active={hasScrolled} />
                    </ShadowClip>
                  </ShadowPosition>
                  <Content
                    allowContentScroll={
                      allowContentScroll ?? RESPONSIVE_VARIANT_DEFAULTS[responsiveVariant].allowContentScroll
                    }
                    contentPadding={contentPadding}
                    enforceResponsiveMobileDialog={enforceResponsiveMobileDialog}
                    hasHeader={hasHeader}
                    isDesktop={isDesktop}
                    data-testid="modal-content"
                    responsiveVariant={responsiveVariant}
                  >
                    <ScrollDetector
                      ref={ref => {
                        target.current = ref
                      }}
                    />
                    <Flex flexGrow={1} flexDirection="column">
                      {children}
                    </Flex>
                  </Content>
                </ArcadeModalActionProvider>
              </ArcadeModalContextProvider>
            </ResponsiveContainer>
          </Dimmer>
        </FocusLock>
      </>
    )
  }
)

ArcadeModal.displayName = 'ArcadeModal'

export default ArcadeModal
